import { Dispatch, SetStateAction, useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyEVDCDeviceFormData,
    mapEvdcDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/evdcSpec"
import { EVDCDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_AMENDMENT_CONFIG } from "../../../swrConfig"
import { NewEvdcDeviceAmendmentResponse } from "../../../types/devices/lct/publishEvdcDeviceTypes"
import { START_NEW_LCT_DEVICE_AMENDMENT } from "../../../urls/private"

interface IUseStartNewEvdcDeviceAmendmentOutput {
    data: EVDCDeviceFormData
    setData: Dispatch<SetStateAction<EVDCDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    error?: AppError
}

const startNewEvdcDeviceAmendmentMapper = getTsIoMapper(
    NewEvdcDeviceAmendmentResponse,
    "startNewEvdcDeviceAmendmentMapper"
)

/**
 * Hook for starting a new EVDC device amendment.
 * Returns data for the device to amend, which is then used to populate the device context
 */
const useStartNewEvdcDeviceAmendment = (
    deviceRef: string,
    attachmentUUID: string
): IUseStartNewEvdcDeviceAmendmentOutput => {
    const [data, setData] = useState<EVDCDeviceFormData>(
        getEmptyEVDCDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_AMENDMENT(
            deviceRef,
            attachmentUUID,
            LCTOnlyDatasets.EVDC
        ),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes =
                    startNewEvdcDeviceAmendmentMapper.map(response)
                setData(mapEvdcDeviceResponseToDeviceFormData(mappedRes))
            } catch (err) {
                throw invalidApiDataError(
                    "Failed to start new EVDC amendment - data load failed"
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_AMENDMENT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewEvdcDeviceAmendment
